import { useState, useCallback, useEffect, useRef } from 'react';
import { createApiUrl } from '../config/api';
import { useApiAuth } from './useApiAuth';
import type { PlanData } from '../types/tabs';

interface UsePlanActionsProps {
  sessionId: string | null;
  onPlanUpdate?: (planData: PlanData) => void;
}

/**
 * Hook for plan actions.
 * Plan data is now received through the DataResponse streaming system,
 * so this hook only handles mutations.
 */
export const usePlanActions = ({ sessionId, onPlanUpdate }: UsePlanActionsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAuthHeaders } = useApiAuth();
  const getAuthHeadersRef = useRef(getAuthHeaders);

  // Keep ref updated
  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders;
  }, [getAuthHeaders]);

  const updatePlan = useCallback(async (planData: PlanData) => {
    if (!sessionId) return false;

    setIsLoading(true);
    setError(null);
    try {
      const authHeaders = await getAuthHeadersRef.current();
      const response = await fetch(
        createApiUrl(`/plan/session/${sessionId}`),
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify(planData),
        }
      );

      if (response.ok) {
        const updatedPlan = await response.json();
        if (onPlanUpdate) {
          onPlanUpdate(updatedPlan);
        }
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to update plan');
        return false;
      }
    } catch (err) {
      console.error('Error updating plan:', err);
      setError('Failed to update plan');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, onPlanUpdate]);
`2  q12 q`
  return {
    isLoading,
    error,
    updatePlan,
  };
};
